import PropTypes from 'prop-types';
import React, { useCallback, useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';

import Abacus from 'components/common/icons/nav/abacus';
import AnswerPerformanceView from 'models/location/answer_performance_view';
import BookOpen from 'components/common/icons/nav/book_open';
import ChartLine from 'components/common/icons/nav/chart_line';
import ChevronDownIcon from 'components/lib/icons/chevron_down_icon';
import Cog from 'components/common/icons/nav/cog';
import CommentLines from 'components/common/icons/nav/comment_lines';
import Compass from 'components/common/icons/nav/compass';
import connect from 'components/lib/connect';
import GuidePerformanceView from 'models/location/guide_performance_view';
import Home from 'components/common/icons/nav/home';
import Inbox from 'models/location/inbox';
import KbAdmin from 'models/location/kb_admin';
import Link from 'components/common/link';
import LinkOutStroke from 'components/common/icons/stroke/link-out-stroke';
import List from 'components/common/icons/nav/list';
import Liveboards from 'models/location/liveboards';
import NavigateTo from 'actions/current_location/navigate_to';
import Random from 'components/common/icons/nav/random';
import Reports from 'models/location/reports';
import Search from 'components/common/icons/nav/search';
import ShareAlt from 'components/common/icons/nav/share_alt';
import Sitemap from 'components/common/icons/nav/sitemap';
import StarFillIcon from 'components/common/icons/fill/start_fill_icon';
import StarStrokeIcon from 'components/common/icons/stroke/star_stroke';
import ThLarge from 'components/common/icons/nav/th_large';
import ThumbsUp from 'components/common/icons/nav/thumbs_up';
import ToggleNavItemPin from 'actions/agent_preferences/toggle_nav_item_pin';
import VisuallyHidden from 'components/common/visually_hidden';
import WaveSquare from 'components/common/icons/nav/wave_square';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';
import { getLocationUrl } from 'scripts/adapters/routes/location_url';
import { HotkeysShownContext } from 'components/contexts/hotkeys';
import { navItemId } from 'components/root/nav_item_id';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export function NavigationMenu({ agentPreferences, hasGuideCollections, isSidekickEnabled, onClose }) {
  const { t } = useTranslation();
  const isFeatureEnabled = useIsFeatureEnabled();
  const { dispatchHotkeysShown } = useContext(HotkeysShownContext);
  const showHotkeys = useCallback(() => dispatchHotkeysShown('on'), [dispatchHotkeysShown]);
  const executeAction = useExecuteAction();
  const onTogglePin = useCallback(id => executeAction(ToggleNavItemPin, { id }), [executeAction]);

  // satisfies at least one feature
  const satisfiesFeatures = features => {
    if (typeof features === 'string') features = [features];
    return features.some(feature => isFeatureEnabled(feature));
  };

  function getJourneyChoice() {
    if (!isSidekickEnabled) return null;
    if (!satisfiesFeatures(['viewGuidePerformance', 'viewAnswerPerformance', 'answers:admin', 'answers:read'])) {
      return null;
    }
    const useGuide = hasGuideCollections && satisfiesFeatures('viewGuidePerformance');
    return {
      dataAid: useGuide ? 'guide-performance' : 'answer-performance',
      locationModel: useGuide ? GuidePerformanceView : AnswerPerformanceView,
    };
  }

  function renderJourneysLink(opts = {}) {
    const choice = getJourneyChoice();
    if (!choice) return null;
    return (
      <MenuOption
        data-aid={choice.dataAid}
        icon={opts.icon}
        locationModel={choice.locationModel}
        onClick={onClose}
        title={t('Journeys')}
      />
    );
  }

  if (isFeatureEnabled('newMainNav')) {
    return renderNewNavigation({
      agentPreferences,
      getJourneyChoice,
      isSidekickEnabled,
      onClose,
      onTogglePin,
      satisfiesFeatures,
      showHotkeys,
      t,
    });
  }

  function renderSidekickMenuOptions() {
    if (!isSidekickEnabled || !satisfiesFeatures('sidekickConversationManagement')) {
      return null;
    }

    return (
      <>
        <StyledMenuItem data-aid="sidekick-conversations">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/communication-sessions"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/communication-sessions');
            }}
          >
            {t('Gladly AI Sessions')}
          </StyledLink>
        </StyledMenuItem>
        <StyledMenuItem data-aid="sidekick-reviews">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/reviews"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/reviews');
            }}
          >
            {t('Reviews')}
          </StyledLink>
        </StyledMenuItem>
        <StyledMenuItem data-aid="sidekick-guides">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/guides"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/guides');
            }}
          >
            {t('Guides')}
          </StyledLink>
        </StyledMenuItem>
      </>
    );
  }

  return (
    <div className={`navigationMenu navigationMenuV2`}>
      <StyledLine />
      {satisfiesFeatures(['conversation:read']) && (
        <MenuOption locationModel={Inbox} locationPath="" onClick={onClose} title={t('Home')} />
      )}
      {satisfiesFeatures(['answers:admin', 'answers:read']) && (
        <MenuOption locationModel={KbAdmin} onClick={onClose} title={t('Answers')} />
      )}

      {renderJourneysLink()}

      {renderSidekickMenuOptions()}

      {satisfiesFeatures('reports:read') && (
        <MenuOption locationModel={Reports} onClick={onClose} title={t('Reports')} />
      )}
      {satisfiesFeatures('viewLiveboards') && (
        <MenuOption locationModel={Liveboards} onClick={onClose} title={t('Liveboards')} />
      )}
      <StyledLine />
      {satisfiesFeatures([
        'adminSettings',
        'devManagement',
        'debuggingView',
        'answers:admin',
        'topics:admin',
        'auditLog:read',
        'agentExperience:admin',
        'channels:admin',
        'orchestration:admin',
        'proactiveConversations:admin',
      ]) && (
        <StyledMenuItem data-aid="settings-menu">
          <StyledLink
            className="navigationMenu-option"
            href="/admin/home"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/admin/home');
            }}
          >
            {t('Settings')}
          </StyledLink>
        </StyledMenuItem>
      )}
      <StyledLine />
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-new-features"
          href="https://help.gladly.com/docs/whats-new"
          tabIndex="-1"
          target="release-notes"
        >
          {t("What's new in Gladly")}
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-help-link"
          href="https://help.gladly.com"
          tabIndex="-1"
          target="help"
        >
          {t('Help Docs')}
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-academy-link"
          href="https://academy.gladly.com"
          tabIndex="-1"
          target="academy"
        >
          {t('Gladly Academy')}
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      {satisfiesFeatures('contactSupport') && (
        <StyledMenuItem data-aid="contact-support-menu">
          <StyledExternalLink
            className="navigationMenu-option"
            data-aid="navigationMenu-help-link"
            href="/support.html"
            tabIndex="-1"
            target="support"
          >
            {t('Contact Support')}
            <StyledLinkOutStroke />
          </StyledExternalLink>
        </StyledMenuItem>
      )}
      <StyledLine />
      <StyledMenuItem>
        <StyledLink
          className="navigationMenu-option"
          data-aid="navigationMenu-shortcuts-link"
          onClick={showHotkeys}
          tabIndex="-1"
        >
          {t('Keyboard Shortcuts')}
        </StyledLink>
      </StyledMenuItem>
      <PrivacyPolicySection>
        <PrivacyPolicyLink
          data-aid="navigationMenu-privacy-policy-link"
          href="https://www.gladly.com/privacy-policy/"
          rel="noopener noreferrer"
          tabIndex="-1"
          target="_blank"
        >
          {t('Privacy Policy')}
        </PrivacyPolicyLink>
      </PrivacyPolicySection>
    </div>
  );
}

NavigationMenu.propTypes = {
  agentPreferences: PropTypes.object.isRequired,
  hasGuideCollections: PropTypes.bool.isRequired,
  isSidekickEnabled: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const NavigationMenuContainer = connect(mapStateToProps)(NavigationMenu);
export default NavigationMenuContainer;

function mapStateToProps({ getProvider }, { onClose }) {
  const organization = getProvider('organization').get();
  // Observe currentLocation so the menu re-renders on navigation. MenuOption's
  // active-state check reads window.location.pathname; without this
  // subscription the connect HOC's binding-observation can skip re-rendering
  // when the agent navigates, leaving the active highlight stale.
  const currentLocation = getProvider('currentLocation').get();
  return {
    onClose,
    agentPreferences: getProvider('agentPreferences').get(),
    hasGuideCollections: getProvider('guideCollections').findAll().length > 0,
    isSidekickEnabled: !!organization?.isSidekickEnabled,
    currentLocation,
  };
}

function renderNewNavigation({
  agentPreferences,
  getJourneyChoice,
  isSidekickEnabled,
  onClose,
  onTogglePin,
  satisfiesFeatures,
  showHotkeys,
  t,
}) {
  const isExternalActive = path => window.location.pathname.startsWith(path);

  const goExternal = path => evt => {
    evt.preventDefault();
    window.location.assign(path);
  };

  const journey = getJourneyChoice();
  const showHome = satisfiesFeatures(['conversation:read']);
  const showAnswers = satisfiesFeatures(['answers:admin', 'answers:read']);
  const showLiveboards = satisfiesFeatures('viewLiveboards');
  const showReports = satisfiesFeatures('reports:read');
  const showSidekickItems = isSidekickEnabled && satisfiesFeatures('sidekickConversationManagement');
  const showSettings = satisfiesFeatures([
    'adminSettings',
    'devManagement',
    'debuggingView',
    'answers:admin',
    'topics:admin',
    'auditLog:read',
    'agentExperience:admin',
    'channels:admin',
    'orchestration:admin',
    'proactiveConversations:admin',
  ]);

  const hasTeam = showHome || showLiveboards || showAnswers || showReports;
  // The "Gladly AI" section is the Sidekick brand surface — gate the whole
  // tree on isSidekickEnabled so non-Sidekick orgs don't see an empty shell
  // of stub items.
  const hasGladlyAi = isSidekickEnabled;

  // Items the PR (sagansystems/glados#7765) gates by role rather than flag —
  // `teamManagement` is granted to team-manager / system-admin / compliance-admin,
  // matching the PR's `requires: ['system-admin', 'compliance-admin', 'team-manager']`.
  // Signals additionally requires the `sentinel` flag: its destination route
  // (glados `/sidekick/signals`) hard-404s when `sentinel` is off, so gating on
  // role alone would surface a dead link in non-sentinel orgs.
  const showSignals = satisfiesFeatures('teamManagement') && satisfiesFeatures('sentinel');
  const showSimulations = showSidekickItems && satisfiesFeatures('teamManagement');
  // Same shape as Signals: role-gated, plus the `sessionExplorerForAgents`
  // flag its destination route (glados `/sidekick/performance/explorer`)
  // 404s without. glados's own SideNav is the source of truth for this exact
  // role list (getNewNavLinks.ts).
  const showExplorer = satisfiesFeatures('teamManagement') && satisfiesFeatures('sessionExplorerForAgents');

  const isPinned = id => agentPreferences.isNavItemPinned(id);
  const pinProps = id => ({ pinId: id, isPinned: isPinned(id), onTogglePin });

  // Outer `hasX` gates keep items from a hidden source section out of Starred —
  // each spread evaluates to [] when its section is off.
  const pinnableItems = [
    ...(hasGladlyAi
      ? [
          showSidekickItems && {
            pinId: navItemId('Gladly AI', 'Analyze', 'AI Sessions'),
            kind: 'external',
            props: {
              dataAid: 'sidekick-conversations',
              href: '/sidekick/communication-sessions',
              icon: CommentLines,
              label: t('AI Sessions'),
              isActive: isExternalActive('/sidekick/communication-sessions'),
              onClick: goExternal('/sidekick/communication-sessions'),
            },
          },
          showSidekickItems && {
            pinId: navItemId('Gladly AI', 'Analyze', 'Reviews'),
            kind: 'external',
            props: {
              dataAid: 'sidekick-reviews',
              href: '/sidekick/reviews',
              icon: ThumbsUp,
              label: t('Reviews'),
              isActive: isExternalActive('/sidekick/reviews'),
              onClick: goExternal('/sidekick/reviews'),
            },
          },
          showExplorer && {
            pinId: navItemId('Gladly AI', 'Analyze', 'Explorer'),
            kind: 'external',
            props: {
              dataAid: 'sidekick-session-explorer',
              href: '/sidekick/performance/explorer',
              icon: Search,
              label: t('Explorer'),
              isActive: isExternalActive('/sidekick/performance/explorer'),
              onClick: goExternal('/sidekick/performance/explorer'),
            },
          },
          showSignals && {
            pinId: navItemId('Gladly AI', 'Analyze', 'Signals'),
            kind: 'external',
            props: {
              dataAid: 'gladly-ai-signals',
              href: '/sidekick/signals',
              icon: WaveSquare,
              label: t('Signals'),
              isActive: isExternalActive('/sidekick/signals'),
              onClick: goExternal('/sidekick/signals'),
            },
          },
          journey && {
            pinId: navItemId('Gladly AI', 'Analyze', 'Journeys'),
            kind: 'menu',
            props: {
              ...journey,
              icon: Random,
              onClick: onClose,
              title: t('Journeys'),
            },
          },
          showSidekickItems && {
            pinId: navItemId('Gladly AI', 'Manage', 'AI Agents'),
            kind: 'external',
            props: {
              dataAid: 'sidekick-guides',
              href: '/sidekick/guides',
              icon: Compass,
              label: t('AI Agents'),
              isActive: isExternalActive('/sidekick/guides'),
              onClick: goExternal('/sidekick/guides'),
            },
          },
          showSidekickItems && {
            pinId: navItemId('Gladly AI', 'Manage', 'Workflows'),
            kind: 'external',
            props: {
              dataAid: 'sidekick-workflows',
              href: '/sidekick/workflows',
              icon: Sitemap,
              label: t('Workflows'),
              isActive: isExternalActive('/sidekick/workflows'),
              onClick: goExternal('/sidekick/workflows'),
            },
          },
          showAnswers && {
            pinId: navItemId('Gladly AI', 'Manage', 'Answers'),
            kind: 'menu',
            props: { icon: BookOpen, locationModel: KbAdmin, onClick: onClose, title: t('Answers') },
          },
          showSimulations && {
            pinId: navItemId('Gladly AI', 'Test', 'Simulations'),
            kind: 'external',
            props: {
              dataAid: 'sidekick-simulations',
              href: '/sidekick/guides?panel=scenarios',
              icon: Abacus,
              label: t('Simulations'),
              isActive: isExternalActive('/sidekick/guides') && window.location.search.includes('panel=scenarios'),
              onClick: goExternal('/sidekick/guides?panel=scenarios'),
            },
          },
          showSidekickItems && {
            pinId: navItemId('Gladly AI', 'Deploy', 'Changelog'),
            kind: 'external',
            props: {
              dataAid: 'sidekick-changelog',
              href: '/sidekick/audit-history',
              icon: List,
              label: t('Changelog'),
              isActive: isExternalActive('/sidekick/audit-history'),
              onClick: goExternal('/sidekick/audit-history'),
            },
          },
          showSidekickItems && {
            pinId: navItemId('Gladly AI', 'Deploy', 'Entry Points'),
            kind: 'external',
            props: {
              dataAid: 'sidekick-entry-points',
              href: '/sidekick/entry-points',
              icon: ShareAlt,
              label: t('Entry Points'),
              isActive: isExternalActive('/sidekick/entry-points'),
              onClick: goExternal('/sidekick/entry-points'),
            },
          },
        ]
      : []),
    ...(hasTeam
      ? [
          showHome && {
            pinId: navItemId('Gladly Team', undefined, 'Home'),
            kind: 'menu',
            props: { icon: Home, locationModel: Inbox, locationPath: '', onClick: onClose, title: t('Home') },
          },
          showLiveboards && {
            pinId: navItemId('Gladly Team', undefined, 'Liveboards'),
            kind: 'menu',
            props: { icon: ThLarge, locationModel: Liveboards, onClick: onClose, title: t('Liveboards') },
          },
          showAnswers && {
            pinId: navItemId('Gladly Team', undefined, 'Answers'),
            kind: 'menu',
            props: { icon: BookOpen, locationModel: KbAdmin, onClick: onClose, title: t('Answers') },
          },
          showReports && {
            pinId: navItemId('Gladly Team', undefined, 'Reports'),
            kind: 'menu',
            props: { icon: ChartLine, locationModel: Reports, onClick: onClose, title: t('Reports') },
          },
        ]
      : []),
    ...(showSettings
      ? [
          {
            pinId: navItemId('Admin', undefined, 'Settings'),
            kind: 'external',
            props: {
              dataAid: 'settings-menu',
              href: '/admin/home',
              icon: Cog,
              label: t('Settings'),
              isActive: isExternalActive('/admin'),
              onClick: goExternal('/admin/home'),
            },
          },
        ]
      : []),
  ].filter(Boolean);

  // Render Starred in pin-time order (oldest first — `toggleNavItemPin`
  // appends new ids), not source-menu order. `.find` lookup also drops
  // stale ids (items the agent no longer sees through the gates above).
  const pinnedItems = agentPreferences.pinnedNavItemIds
    .map(id => pinnableItems.find(item => item.pinId === id))
    .filter(Boolean);

  const renderPinnable = (item, variant) => {
    const Component = item.kind === 'external' ? ExternalNavItem : MenuOption;
    // Drop the row's `dataAid` on the Starred copy to keep `[data-aid=...]` selectors unique.
    const props = variant === 'starred' ? { ...item.props, dataAid: undefined } : item.props;
    return <Component {...props} {...pinProps(item.pinId)} variant={variant} />;
  };
  const renderItems = (items, variant) => items.map(item => <li key={item.pinId}>{renderPinnable(item, variant)}</li>);
  const renderSubSection = (heading, subSlug, items) => {
    if (items.length === 0) return null;
    return (
      <React.Fragment key={subSlug}>
        <SubSectionHeading id={`nav-subsection-${subSlug}`}>{heading}</SubSectionHeading>
        <NewSubItemList aria-labelledby={`nav-subsection-${subSlug}`}>{renderItems(items, 'source')}</NewSubItemList>
      </React.Fragment>
    );
  };

  // Pinned items render twice: once in Starred (shortcut, at the top) and
  // again in their source sub-section (canonical location). This mirrors
  // the glados drawer — Starred is a quick-access surface, not a move.
  const inSection = prefix => item => item.pinId.startsWith(prefix);
  const aiAnalyze = pinnableItems.filter(inSection('gladly-ai/analyze/'));
  const aiManage = pinnableItems.filter(inSection('gladly-ai/manage/'));
  const aiTest = pinnableItems.filter(inSection('gladly-ai/test/'));
  const aiDeploy = pinnableItems.filter(inSection('gladly-ai/deploy/'));
  const teamItems = pinnableItems.filter(inSection('gladly-team/'));
  const adminItems = pinnableItems.filter(inSection('admin/'));

  return (
    <NewNavRoot aria-label={t('Main navigation')} className="navigationMenu navigationMenuV2">
      <StyledLine />

      {pinnedItems.length > 0 && (
        <CollapsibleSection headingId="nav-section-starred" listId="nav-section-starred-list" title={t('Starred')}>
          {renderItems(pinnedItems, 'starred')}
        </CollapsibleSection>
      )}

      {hasGladlyAi && (
        <CollapsibleSection
          headingId="nav-section-gladly-ai"
          listId="nav-section-gladly-ai-list"
          title={t('Gladly AI')}
        >
          {renderSubSection(<Trans>Analyze</Trans>, 'analyze', aiAnalyze)}
          {renderSubSection(<Trans>Manage</Trans>, 'manage', aiManage)}
          {renderSubSection(<Trans>Test</Trans>, 'test', aiTest)}
          {renderSubSection(<Trans>Deploy</Trans>, 'deploy', aiDeploy)}
        </CollapsibleSection>
      )}

      {hasTeam && (
        <CollapsibleSection headingId="nav-section-team" listId="nav-section-team-list" title={t('Gladly Team')}>
          {renderItems(teamItems, 'source')}
        </CollapsibleSection>
      )}

      {showSettings && (
        <CollapsibleSection headingId="nav-section-admin" listId="nav-section-admin-list" title={t('Admin')}>
          {renderItems(adminItems, 'source')}
        </CollapsibleSection>
      )}

      <ResourcesSection showContactSupport={satisfiesFeatures('contactSupport')} />
      <KeyboardShortcutsList>
        <li>
          <StyledMenuItem>
            <StyledLink
              className="navigationMenu-option"
              data-aid="navigationMenu-shortcuts-link"
              onClick={showHotkeys}
              tabIndex="-1"
            >
              <Trans>Keyboard Shortcuts</Trans>
            </StyledLink>
          </StyledMenuItem>
        </li>
      </KeyboardShortcutsList>
      <NewPrivacyPolicySection>
        <PrivacyPolicyLink
          data-aid="navigationMenu-privacy-policy-link"
          href="https://www.gladly.com/privacy-policy/"
          rel="noopener noreferrer"
          tabIndex="-1"
          target="_blank"
        >
          <Trans>Privacy Policy</Trans>
          <VisuallyHidden>
            {' '}
            <Trans>(opens in new tab)</Trans>
          </VisuallyHidden>
        </PrivacyPolicyLink>
      </NewPrivacyPolicySection>
    </NewNavRoot>
  );
}

const NavIcon = ({ icon: IconComponent }) => (
  <NavIconBox aria-hidden="true">
    <IconComponent />
  </NavIconBox>
);

NavIcon.propTypes = {
  icon: PropTypes.elementType.isRequired,
};

const ExternalNavItem = ({ dataAid, href, icon, isActive, isPinned, label, onClick, onTogglePin, pinId, variant }) => (
  <StyledMenuItem data-aid={dataAid} isActive={isActive}>
    <StyledLink
      aria-current={isActive ? 'page' : undefined}
      className="navigationMenu-option"
      href={href}
      onClick={onClick}
    >
      {icon && <NavIcon icon={icon} />}
      {label}
    </StyledLink>
    {pinId && <PinButton isPinned={isPinned} label={label} onTogglePin={onTogglePin} pinId={pinId} variant={variant} />}
  </StyledMenuItem>
);

ExternalNavItem.propTypes = {
  dataAid: PropTypes.string,
  href: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  isActive: PropTypes.bool,
  isPinned: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onTogglePin: PropTypes.func,
  pinId: PropTypes.string,
  variant: PropTypes.oneOf(['starred', 'source']),
};

export const MenuOption = ({ dataAid, icon, isPinned, locationModel, onClick, onTogglePin, pinId, title, variant }) => {
  const executeAction = useExecuteAction();
  const handleClick = useCallback(
    evt => {
      evt.preventDefault();
      executeAction(NavigateTo, locationModel.create());
      onClick();
    },
    [executeAction, locationModel, onClick]
  );

  // Some location models (e.g. GuidePerformanceView) default query params, so
  // strip the query before segment comparison — otherwise the first path
  // segment would include "?..." and never match the bare pathname segment.
  const menuPath = getLocationUrl(locationModel.create()).split('?')[0];
  const pathname = window.location.pathname;
  // Director.js maps the root path "/" to the home action without rewriting the URL.
  // Treat "/" as "/home" so the Home menu item highlights when the agent lands on home.
  const isAtRoot = pathname === '/' || pathname === '';
  const currentLocation = isAtRoot ? 'home' : pathname.split('/')[1];
  const isActiveItem = menuPath.split('/')[1] === currentLocation;
  return (
    <StyledMenuItem data-aid={dataAid} isActive={isActiveItem}>
      <StyledLink
        aria-current={isActiveItem ? 'page' : undefined}
        className="navigationMenu-option"
        href={menuPath}
        onClick={handleClick}
      >
        {icon && <NavIcon icon={icon} />}
        {title}
      </StyledLink>
      {pinId && (
        <PinButton isPinned={isPinned} label={title} onTogglePin={onTogglePin} pinId={pinId} variant={variant} />
      )}
    </StyledMenuItem>
  );
};

MenuOption.propTypes = {
  dataAid: PropTypes.string,
  icon: PropTypes.elementType,
  isPinned: PropTypes.bool,
  locationModel: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onTogglePin: PropTypes.func,
  pinId: PropTypes.string,
  title: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['starred', 'source']),
};

const CollapsibleSection = ({ children, defaultOpen = true, headingId, listId, title }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <>
      <SectionToggle
        aria-controls={listId}
        aria-expanded={isOpen}
        data-aid={`${headingId}-toggle`}
        id={headingId}
        onClick={() => setIsOpen(open => !open)}
        type="button"
      >
        {title}
        <SectionToggleChevron $isOpen={isOpen} aria-hidden="true">
          <ChevronDownIcon />
        </SectionToggleChevron>
      </SectionToggle>
      {isOpen && (
        <NewItemList aria-labelledby={headingId} id={listId}>
          {children}
        </NewItemList>
      )}
    </>
  );
};

CollapsibleSection.propTypes = {
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
  headingId: PropTypes.string.isRequired,
  listId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const ResourcesSection = ({ showContactSupport }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const headingId = 'nav-section-resources';
  const listId = 'nav-section-resources-list';
  return (
    <>
      <SectionToggle
        aria-controls={listId}
        aria-expanded={isOpen}
        data-aid="navigationMenu-resources-toggle"
        id={headingId}
        onClick={() => setIsOpen(open => !open)}
        type="button"
      >
        {t('Resources')}
        <SectionToggleChevron $isOpen={isOpen} aria-hidden="true">
          <ChevronDownIcon />
        </SectionToggleChevron>
      </SectionToggle>
      {isOpen && (
        <NewItemList aria-labelledby={headingId} id={listId}>
          <li>
            <StyledMenuItem>
              <StyledExternalLink
                className="navigationMenu-option"
                data-aid="navigationMenu-new-features"
                href="https://help.gladly.com/docs/whats-new"
                tabIndex="-1"
                target="release-notes"
              >
                {t("What's new in Gladly")}
                <StyledLinkOutStroke aria-hidden="true" />
                <VisuallyHidden>{t('(opens in new tab)')}</VisuallyHidden>
              </StyledExternalLink>
            </StyledMenuItem>
          </li>
          <li>
            <StyledMenuItem>
              <StyledExternalLink
                className="navigationMenu-option"
                data-aid="navigationMenu-help-link"
                href="https://help.gladly.com"
                tabIndex="-1"
                target="help"
              >
                {t('Help Docs')}
                <StyledLinkOutStroke aria-hidden="true" />
                <VisuallyHidden>{t('(opens in new tab)')}</VisuallyHidden>
              </StyledExternalLink>
            </StyledMenuItem>
          </li>
          <li>
            <StyledMenuItem>
              <StyledExternalLink
                className="navigationMenu-option"
                data-aid="navigationMenu-academy-link"
                href="https://academy.gladly.com"
                tabIndex="-1"
                target="academy"
              >
                {t('Gladly Academy')}
                <StyledLinkOutStroke aria-hidden="true" />
                <VisuallyHidden>{t('(opens in new tab)')}</VisuallyHidden>
              </StyledExternalLink>
            </StyledMenuItem>
          </li>
          {showContactSupport && (
            <li>
              <StyledMenuItem data-aid="contact-support-menu">
                <StyledExternalLink
                  className="navigationMenu-option"
                  data-aid="navigationMenu-help-link"
                  href="/support.html"
                  tabIndex="-1"
                  target="support"
                >
                  {t('Contact Support')}
                  <StyledLinkOutStroke aria-hidden="true" />
                  <VisuallyHidden>{t('(opens in new tab)')}</VisuallyHidden>
                </StyledExternalLink>
              </StyledMenuItem>
            </li>
          )}
        </NewItemList>
      )}
    </>
  );
};

ResourcesSection.propTypes = {
  showContactSupport: PropTypes.bool.isRequired,
};

const PinButton = ({ isPinned, label, onTogglePin, pinId, variant }) => {
  const { t } = useTranslation();
  const handleClick = useCallback(
    evt => {
      evt.preventDefault();
      evt.stopPropagation();
      onTogglePin(pinId);
    },
    [onTogglePin, pinId]
  );
  const Icon = variant === 'starred' ? StarFillIcon : StarStrokeIcon;
  return (
    <PinToggleButton
      $isPinned={isPinned}
      $variant={variant}
      aria-label={isPinned ? t('Unpin {{label}}', { label }) : t('Pin {{label}}', { label })}
      aria-pressed={isPinned}
      data-aid={`${variant === 'starred' ? 'starred-pin' : 'pin'}-${pinId.replace(/\//g, '-')}`}
      onClick={handleClick}
      type="button"
    >
      <Icon />
    </PinToggleButton>
  );
};

PinButton.propTypes = {
  isPinned: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onTogglePin: PropTypes.func.isRequired,
  pinId: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['starred', 'source']).isRequired,
};

const ActiveItem = css`
  background-color: ${p => p.theme.colors.gray200};
  font-weight: bold;
  &:before {
    background-color: ${p => p.theme.colors.green500};
    bottom: 0;
    content: '';
    left: 0;
    margin: auto;
    position: absolute;
    top: 0;
    width: 4px;
  }
`;

const StyledLinkOutStroke = styled(LinkOutStroke)`
  height: 14px;
  width: 14px;
  margin-left: 4px;
`;

const NavIconBox = styled.span`
  align-items: center;
  color: ${p => p.theme.colors.gray800};
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  margin-right: ${p => p.theme.spacing.inlineSmall};
  width: 24px;

  svg {
    fill: currentColor;
    height: 16px;
    width: 16px;

    path {
      fill: currentColor;
    }
  }
`;

// Flex column so the privacy policy can use margin-top: auto to anchor at the
// bottom of the menu when content is short, while still flowing in-line
// (and scrolling) when content is long. Avoids the legacy
// position: absolute approach where privacy policy floats over the items.
const NewNavRoot = styled.nav`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const NewItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  &:empty {
    display: none;
  }
`;

const SubSectionHeading = styled.h3`
  color: ${p => p.theme.colors.gray700};
  font-size: ${p => p.theme.fontSize.base};
  font-weight: bold;
  margin: 0;
  padding: ${p => p.theme.spacing.medium} ${p => p.theme.spacing.insetLarge} ${p => p.theme.spacing.small};
`;

const NewSubItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  &:empty {
    display: none;
  }

  a.navigationMenu-option {
    padding-left: 24px;
  }
`;

// Resources collapse/expand toggle. Headed as <h2> visually but rendered as a
// <button> inside it for keyboard a11y. We use a button element directly
// (instead of nesting <button> inside <h2>) because the toggle is the only
// labelable thing here and screen readers already announce the heading via
// aria-labelledby on the controlled list.
const SectionToggle = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: ${p => p.theme.colors.gray800};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: ${p => p.theme.fontSize.medium};
  font-weight: bold;
  justify-content: space-between;
  margin: 12px 0 0;
  padding: 8px 24px;
  text-align: left;
  width: 100%;

  &:hover {
    background-color: ${p => p.theme.colors.green100};
  }
`;

const SectionToggleChevron = styled.span`
  align-items: center;
  color: ${p => p.theme.colors.gray800};
  display: inline-flex;
  height: 12px;
  justify-content: center;
  margin-left: 8px;
  transform: rotate(${p => (p.$isOpen ? '0deg' : '-90deg')});
  transition: transform 0.15s ease;
  width: 12px;

  svg {
    fill: currentColor;
    height: 12px;
    width: 12px;

    path {
      fill: currentColor;
    }
  }
`;

// Keyboard Shortcuts is the first of the two bottom-anchored items
// (Keyboard Shortcuts + Privacy Policy). margin-top: auto inside the
// flex-column NewNavRoot pushes both to the bottom when content is short,
// while still flowing in-line (and scrolling) when content is long.
// Color is overridden to gray800 to match the Privacy Policy footer link.
const KeyboardShortcutsList = styled(NewItemList)`
  margin-top: auto;

  a,
  a:hover {
    color: ${p => p.theme.colors.gray800};
  }
`;

// Privacy policy sits directly under Keyboard Shortcuts (which has
// margin-top: auto), so the pair anchors to the bottom of the menu when
// content is short and falls back to in-flow scrolling when it's not.
const NewPrivacyPolicySection = styled.div`
  padding: 8px 0;
`;

export const StyledLink = styled(Link)`
  color: ${p => p.theme.colors.black};
  align-items: center;
  display: flex;
  flex: 1;
  min-width: 0;
  height: 32px;
  padding: 6px 24px;
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.black};
    text-decoration: none;
  }
`;

const StyledLine = styled.div`
  height: 1px;
  background-color: ${p => p.theme.colors.gray300};
  margin: 2px 0;

  & + & {
    display: none;
  }
`;

const PinToggleButton = styled.button`
  align-items: center;
  align-self: center;
  background: none;
  border: 0;
  color: ${p => {
    if (p.$variant === 'starred') return p.theme.colors.gray800;
    if (p.$isPinned) return p.theme.colors.gray600;
    return p.theme.colors.gray700;
  }};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  height: 12px;
  justify-content: center;
  margin: 0 ${p => p.theme.spacing.insetLarge} 0 ${p => p.theme.spacing.inlineSmall};
  opacity: ${p => (p.$variant === 'starred' || p.$isPinned ? 1 : 0)};
  padding: 0;
  transition: opacity 0.15s ease, color 0.15s ease;
  width: 12px;

  svg {
    height: 12px;
    width: 12px;
  }
`;

const StyledMenuItem = styled.div`
  align-items: stretch;
  cursor: pointer;
  display: flex;
  padding: 0;
  position: relative;
  &:hover {
    background-color: ${p => p.theme.colors.green100};
  }
  &:hover ${PinToggleButton} {
    opacity: 1;
  }
  ${p => p.isActive && ActiveItem}
`;

export const StyledExternalLink = styled.a`
  color: ${p => p.theme.colors.black};
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.green400};
    text-decoration: none;
  }
`;

const PrivacyPolicySection = styled.div`
  bottom: 10px;
  left: 0;
  width: 100%;
  padding: 8px 0;
  position: absolute;
`;

const PrivacyPolicyLink = styled.a`
  color: ${p => p.theme.colors.gray800};
  display: block;
  width: 100%;
  height: 32px;
  padding: 6px 24px;
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.gray800};
    text-decoration: none;
    background-color: ${p => p.theme.colors.green100};
  }
`;
